// START IMPORTS
import { renderMarkdown } from '@a2ui/markdown-it';
import { A2uiSurface, MarkdownContext, basicCatalog } from '@a2ui/react/v0_9';
import { MessageProcessor, A2uiMessage } from '@a2ui/web_core/v0_9';
import { useMemo } from 'react';

import {
  isEmailReportEmpty,
  mapEmailReportPayload,
} from '../utils/mapEmailReportPayload';
import {
  isPhoneReportEmpty,
  mapPhoneReportPayload,
} from '../utils/mapPhoneReportPayload';
import {
  isPropertyReportEmpty,
  mapPropertyReportPayload,
} from '../utils/mapPropertyReportPayload';
import {
  isVehicleReportEmpty,
  mapVehicleReportPayload,
} from '../utils/mapVehicleReportPayload';

import {
  deepSearchReportPreviewCatalog,
  deepSearchSuggestedNextStepsCatalog,
} from './A2uiCatalog';
// END IMPORTS

// START INTERFACES_AND_TYPES
interface A2uiReportRendererProps {
  messages: A2uiMessage[];
}
// END INTERFACES_AND_TYPES

// START CONSTANTS
/**
 * Maps a report surface id to a predicate that decides whether its data model is
 * "empty". An empty report surface — and its paired `<type>_suggestions` surface —
 * are not rendered at all. Add an entry here to extend this to other report types.
 */
const REPORT_EMPTINESS_CHECKS: Record<
  string,
  (value: Record<string, unknown>) => boolean
> = {
  // The renderer doesn't know the vehicle subtype, so a surface counts as empty
  // only when it is empty under both the `vin` and `ymm` mappings, which together
  // cover every subtype-specific field (sales listings, ownership cost, mileage).
  vehicle_report: (value) =>
    isVehicleReportEmpty(mapVehicleReportPayload(value, 'vin')) &&
    isVehicleReportEmpty(mapVehicleReportPayload(value, 'ymm')),
  phone_report: (value) => isPhoneReportEmpty(mapPhoneReportPayload(value)),
  email_report: (value) => isEmailReportEmpty(mapEmailReportPayload(value)),
  property_report: (value) =>
    isPropertyReportEmpty(mapPropertyReportPayload(value)),
};

const REPORT_SUFFIX = '_report';
// END CONSTANTS

export const A2uiReportRenderer = ({ messages }: A2uiReportRendererProps) => {
  // START DERIVED_DATA_AND_UTILS
  const surfaces = useMemo(() => {
    const next = new MessageProcessor([
      basicCatalog,
      deepSearchReportPreviewCatalog,
      deepSearchSuggestedNextStepsCatalog,
    ]);
    next.processMessages(messages);
    return Array.from(next.model.surfacesMap.values());
  }, [messages]);

  // Drop empty report surfaces along with their paired `<type>_suggestions`
  // surface, so an empty report renders nothing at all (no preview, no view
  // report button, no suggested next steps).
  const visibleSurfaces = useMemo(() => {
    const emptyTypes = new Set<string>();
    for (const surface of surfaces) {
      const check = REPORT_EMPTINESS_CHECKS[surface.id];
      if (!check) {
        continue;
      }
      const value = surface.dataModel.get('/') as
        | Record<string, unknown>
        | null
        | undefined;
      // A null/undefined payload has nothing to render, so treat it as empty too.
      if (value == null || check(value)) {
        emptyTypes.add(surface.id.slice(0, -REPORT_SUFFIX.length));
      }
    }

    if (emptyTypes.size === 0) {
      return surfaces;
    }

    return surfaces.filter(
      (surface) =>
        !Array.from(emptyTypes).some(
          (type) =>
            surface.id === `${type}${REPORT_SUFFIX}` ||
            surface.id === `${type}_suggestions`
        )
    );
  }, [surfaces]);
  // END DERIVED_DATA_AND_UTILS

  // START EARLY_RETURNS
  if (visibleSurfaces.length === 0) {
    return null;
  }
  // END EARLY_RETURNS

  // START RENDER
  return (
    <MarkdownContext.Provider value={renderMarkdown}>
      {visibleSurfaces.map((surface) => (
        <A2uiSurface key={surface.id} surface={surface} />
      ))}
    </MarkdownContext.Provider>
  );
  // END RENDER
};
